import React, { forwardRef, useEffect, useRef, useState } from "react";
import _ from "lodash";
import Quill, { QuillOptions } from "quill";
import { App } from "antd";

import { BaseRichTextProps, QuillModules, QuillRef } from "./types";
import BaseError from "../BaseError";

import "./styles.css";
import { useTranslation } from "react-i18next";

const baseModules: QuillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [
      "bold" as const,
      "italic" as const,
      "underline" as const,
      "strike" as const,
      "blockquote" as const,
    ],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link" as const, "formula" as const, "image" as const],
    [{ align: "center" }, { align: "right" }],
    ["code-block" as const],
    ["clean" as const],
  ],
};

const BaseRichText = forwardRef<HTMLDivElement, BaseRichTextProps>(
  (
    {
      value,
      onChange,
      error,
      modules = baseModules,
      maxImageSize = 1048576,
      maxImageWidth = 1920,
      maxImageHeight = 1080,
    }: BaseRichTextProps,
    ref,
  ) => {
    const quillRef = useRef<QuillRef>({
      id: _.uniqueId(),
      element: null,
    });
    const { notification } = App.useApp();
    const { t } = useTranslation();
    const [isInitial, setIsInitial] = useState(true);

    const insertImage = (file: File) => {
      const quill = quillRef.current.element;
      if (!quill) return;

      if (file.size > maxImageSize) {
        notification.error({
          message: t("form.validation.maxFileSizeValidation", {
            fileSize: maxImageSize / 1024 / 1024,
          }),
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target?.result) return;
        const imageDataUrl = e.target.result as string;

        const img = new Image();
        img.onload = () => {
          if (img.width > maxImageWidth || img.height > maxImageHeight) {
            notification.error({
              message: t("form.validation.maxImageDimensionsValidation", {
                width: maxImageWidth,
                height: maxImageHeight,
              }),
            });
            return;
          }

          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", imageDataUrl);
          quill.setSelection(range.index + 1);
        };

        img.onerror = () => {
          notification.error({
            message: t("form.validation.invalidImageFormat"),
          });
        };

        img.src = e.target.result as string;
      };

      reader.onerror = () => {
        notification.error({
          message: t("form.validation.imageReadError"),
        });
      };

      reader.readAsDataURL(file);
    };

    const imageHandler = () => {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.click();

      input.onchange = () => {
        const file = input.files?.[0];
        if (file) {
          insertImage(file);
        }
      };
    };

    useEffect(() => {
      if (!quillRef.current.element) {
        const options: QuillOptions = {
          modules: {
            toolbar: {
              container: modules.toolbar,
              handlers: {
                image: imageHandler,
              },
            },
          },
          theme: "snow",
        };
        const quill = new Quill(`#quill-${quillRef.current.id}`, options);
        quillRef.current.element = quill;

        const editorChangeHandler = () => {
          const content = quill.getSemanticHTML();
          const isEmpty = quill.getLength() === 1;
          onChange(isEmpty ? "" : content);
        };

        quill.on("text-change", editorChangeHandler);

        // Handle paste events
        quill.root.addEventListener("paste", (e: ClipboardEvent) => {
          const items = e.clipboardData?.items;
          if (!items) return;

          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item && item.type.startsWith("image/")) {
              e.preventDefault();
              const file = item.getAsFile();
              if (file) {
                insertImage(file);
              }
            }
          }
        });
      }
    }, [maxImageSize, maxImageWidth, maxImageHeight]);

    useEffect(() => {
      if (quillRef.current.element) {
        if (
          (isInitial || value == null) &&
          value !== "" &&
          value != undefined
        ) {
          quillRef.current.element.clipboard.dangerouslyPasteHTML(value || "");
          quillRef.current.element.blur();
          setIsInitial(false);
        }
      }
    }, [quillRef, isInitial, value]);

    return (
      <div className={`base-rich-text ${error && "base-rich-text-error"}`}>
        <div
          ref={ref}
          id={`quill-${quillRef.current.id}`}
          className="base-rich-text-quill"
        ></div>
        <BaseError error={error} />
      </div>
    );
  },
);

export default BaseRichText;
